import { useContext, useEffect, useMemo, useState } from "react";
import LoggedInBoilerplate from "../LoggedInBoilerplate";
import PermissionGuard from "../../../customUiForCs/PermissionGuard";
import { serverLine } from "../../../../controllers/network/serverLine";
import CustomButton from "../../../helperComponents/CustomButton";
import MaterialInput from "../../../helperComponents/MaterialInput";
import styled from "styled-components";
import { FiPlus, FiEdit2, FiTrash2 } from "react-icons/fi";
import Context from "../../../../Context";
import CustomLabel from "../../../customUiForCs/CustomLabel";
import contractTypesForSite from "../../../../data/employee/contractTypesForSite";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const Card = styled.div`
  border: 1px solid var(--border);
  background: var(--surface3);
  padding: 16px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: var(--element);
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const CardTitle = styled.div`
  font-weight: 700;
  color: var(--element);
  opacity: 0.95;
`;

const CardSub = styled.div`
  color: var(--elementDim);
  font-size: 12px;
`;

const EmptyState = styled.div`
  color: var(--elementDim);
  font-weight: 600;
  padding: 20px;
  text-align: center;
  border: 1px dashed var(--border);
  border-radius: 12px;
  background: var(--surface3);
`;

export default function FunctionsPage() {
  const { employeeFunctions, refreshEmployeeFunctions } = useContext(Context);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [label, setLabel] = useState("");
  const [value, setValue] = useState("");

  const functionsList = useMemo(() => {
    if (employeeFunctions && employeeFunctions.length) return employeeFunctions;
    // Fallback to static list (ensures initial display)
    return contractTypesForSite.map((f) => ({
      id: f.id || f.value || f.label,
      label: f.label || f.value,
      value: f.value || f.label,
    }));
  }, [employeeFunctions]);

  useEffect(() => {
    refreshEmployeeFunctions?.();
  }, []);

  useEffect(() => {
    if (editing) {
      setLabel(editing.label);
      setValue(editing.value);
    } else {
      setLabel("");
      setValue("");
    }
  }, [editing]);

  async function save() {
    if (!label.trim()) return window.popupAlert("Nom requis");
    setLoading(true);
    try {
      await serverLine.post("/functions", {
        id: editing?.id,
        label: label.trim(),
        value: value.trim() || label.trim(),
      });
      setEditing(null);
      setLabel("");
      setValue("");
      await refreshEmployeeFunctions();
      window.popupAlert("Enregistré");
    } catch (e) {
      window.popupAlert(e.message);
    }
    setLoading(false);
  }

  async function remove(item) {
    if (!window.confirm(`Supprimer "${item.label}" ?`)) return;
    setLoading(true);
    try {
      const id = item.id || item.value || item.label;
      if (!id) throw new Error("Function ID manquant");
      await serverLine.delete("/functions", {
        id,
        value: item.value,
        label: item.label,
      });
      await refreshEmployeeFunctions();
      window.popupAlert("Supprimé");
    } catch (e) {
      window.popupAlert(e.message);
    }
    setLoading(false);
  }

  return (
    <PermissionGuard
      permissionName="employeeResponsibilityPermissions"
      action="VIEW"
    >
      <LoggedInBoilerplate
        titleLine1={"Fonctions"}
        titleLine2={"Gestion des fonctions"}
        showBackButton={true}
      >
        <Container>
          <CustomLabel>Ajouter / modifier</CustomLabel>
          <Card>
            <div style={{ display: "flex", gap: "10px", width: "100%" }}>
              <MaterialInput
                label="Nom de la fonction"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
              <MaterialInput
                label="Valeur (optionnel)"
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
            </div>
            <CustomButton
              icon={editing ? <FiEdit2 /> : <FiPlus />}
              onClick={save}
              isLoading={loading}
            >
              {editing ? "Mettre à jour" : "Ajouter"}
            </CustomButton>
          </Card>

          <CustomLabel>Liste des fonctions</CustomLabel>
          {employeeFunctions?.length ? (
            employeeFunctions.map((item) => (
              <Card key={item.id}>
                <div>
                  <CardTitle>{item.label}</CardTitle>
                  <CardSub>{item.value}</CardSub>
                </div>
                <Actions>
                  <CustomButton
                    icon={<FiEdit2 />}
                    onClick={() => setEditing(item)}
                    surfaceColor="var(--surface2)"
                  >
                    Modifier
                  </CustomButton>
                  <CustomButton
                    icon={<FiTrash2 />}
                    onClick={() => remove(item)}
                    surfaceColor="var(--surface2)"
                  >
                    Supprimer
                  </CustomButton>
                </Actions>
              </Card>
            ))
          ) : (
            <EmptyState>Aucune fonction enregistrée.</EmptyState>
          )}
        </Container>
      </LoggedInBoilerplate>
    </PermissionGuard>
  );
}
